import Swiper from 'swiper';
import { Navigation } from 'swiper/modules';
import { createLogger } from '@/lib/logger';
import { Cart, CartEvents, TypedCartEvent } from '../internals/core';
import { CartEventBus } from '../internals/event-bus';
import { MiniCart } from './mini-cart';

class MiniCartRecommendations extends HTMLElement {
  private abortController: AbortController | null = null;
  private eventBus: CartEventBus | null = null;
  private logger = createLogger('mini-cart-recommendations');

  private recommendationsSwiper: Swiper | null = null;

  constructor() {
    super();
  }

  // Add internal logic here as separated functions
  private onCartChanged(e: TypedCartEvent<'cart:changed:debounced'>) {
    const { cart } = e.detail;
    this.render(cart);
  }

  private initSwiper() {
    this.recommendationsSwiper = new Swiper(
      this.querySelector('[data-recommendations-swiper]') as HTMLElement,
      {
        slidesPerView: 'auto',
        spaceBetween: 8,
        slidesOffsetAfter: 20,
        slidesOffsetBefore: 20,
        loop: false,
        modules: [Navigation],
        navigation: {
          prevEl: this.querySelector('.swiper-button-prev') as HTMLElement,
          nextEl: this.querySelector('.swiper-button-next') as HTMLElement,
        },
      },
    );
  }

  private removeSlide(index: number) {
    if (this.recommendationsSwiper) {
      this.recommendationsSwiper.destroy(true, true);

      this.querySelector(`.mini-cart-product-card:nth-child(${index + 1})`)?.remove();

      this.initSwiper();
    }
  }

  private bindAddToCartButtons() {
    const parentMiniCart = this.closest('mini-cart')! as MiniCart;

    function resetLoading(button: Element) {
      button.classList.remove('loading');
      button.textContent = button.getAttribute('data-original-text') || '';
    }

    this.querySelectorAll('.mini-cart-product-card__add-to-cart').forEach(function (button) {
      button.addEventListener('click', function () {
        if (button.classList.contains('loading')) {
          return;
        }

        button.classList.add('loading');
        button.setAttribute('data-original-text', button.textContent || '');
        button.textContent = '';

        const variantId = button.getAttribute('data-variant-id');

        if (variantId) {
          parentMiniCart.cartApi
            .addVariant(parseInt(variantId), 1)
            .then(() => {
              resetLoading(button);
            })
            .catch((err) => {
              resetLoading(button);

              console.error(err);
              alert('Sorry, an error occured.');
            });
        }
      });
    });
  }

  private render(cart: Cart) {
    const self = this;

    const cartContainsMedispaService = !!cart.items.find((item) =>
      item.vendor.toLowerCase().includes('medispa'),
    );

    this.classList.toggle('!hidden', cartContainsMedispaService);

    this.querySelectorAll('.mini-cart-product-card__add-to-cart').forEach(function (button, index) {
      const variantId = button.getAttribute('data-variant-id');
      if (variantId) {
        const variant = cart.items.find((item) => item.variant_id === parseInt(variantId));

        if (variant) {
          self.removeSlide(index);
        }
      }
    });
  }

  private bindEvents() {
    this.eventBus!.on(CartEvents.DebouncedChange, this.onCartChanged.bind(this));
    this.bindAddToCartButtons();
  }

  private unbindEvents() {
    this.eventBus!.off(CartEvents.DebouncedChange, this.onCartChanged.bind(this));
  }

  private onMiniCartReady(parentMiniCart: MiniCart) {
    // Make sure we use the same event bus as the parent mini-cart.
    this.eventBus = parentMiniCart.bus;

    this.bindEvents();
  }

  // Lifecycle hooks
  connectedCallback() {
    this.abortController = new AbortController();

    const parentMiniCart = this.closest('mini-cart')! as MiniCart;
    if (!parentMiniCart) {
      this.logger.error('MiniCartHeader must be a child of a MiniCart element');
      throw new Error('MiniCartHeader must be a child of a MiniCart element');
    }

    parentMiniCart.execOnReady(this.onMiniCartReady.bind(this, parentMiniCart));

    this.initSwiper();
  }

  disconnectedCallback() {
    this.abortController?.abort();

    this.unbindEvents();
  }
}

if (!customElements.get('mini-cart-recommendations')) {
  customElements.define('mini-cart-recommendations', MiniCartRecommendations);
}
